/**
 * Reusable bottom sheet — the overlay + panel pattern extracted from
 * AddPlayerSheet, PlayerActionSheet, and InviteQRSheet.
 *
 * Interface: title, onClose, children. Everything else is layout.
 * The caller owns the content; this module owns the overlay, the
 * rounded panel, safe-area padding, and the close button.
 */

interface BottomSheetProps {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
  readonly maxWidth?: number;
  /**
   * A low sheet that leaves the page usable behind it: no dimmed overlay, no close on a tap
   * outside, and a "Done" button instead of the ×. For entry that the page shows the result
   * of, such as adding players to a squad the scorer is watching fill up.
   */
  readonly compact?: boolean;
  /** Shown just above the panel, outside its scroll area, such as a confirmation. */
  readonly above?: React.ReactNode;
}

export function BottomSheet({ title, onClose, children, maxWidth = 420, compact = false, above }: BottomSheetProps) {
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 700,
        background: compact ? "transparent" : "var(--ds-overlay)", display: "flex",
        alignItems: "flex-end", justifyContent: "center",
        pointerEvents: compact ? "none" : "auto",
      }}
      onClick={compact ? undefined : onClose}
    >
      <div
        role={compact ? "dialog" : undefined}
        aria-label={compact ? title : undefined}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--ds-bg-elevated)",
          borderRadius: "var(--ds-radius-2xl) var(--ds-radius-2xl) 0 0",
          width: "100%", maxWidth,
          maxHeight: compact ? "40dvh" : "min(80vh, 100dvh - 2rem)",
          display: "flex", flexDirection: "column",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          pointerEvents: "auto",
          position: "relative",
          boxShadow: compact ? "var(--ds-shadow-lg, 0 -8px 24px rgba(0, 0, 0, 0.18))" : undefined,
        }}
      >
        {above}
        <div style={{
          padding: compact ? "var(--ds-space-3) var(--ds-space-4) 0" : "var(--ds-space-5) var(--ds-space-5) 0",
          flexShrink: 0,
        }}>
          <div style={{
            display: "flex", justifyContent: "space-between",
            alignItems: "center", marginBottom: compact ? "var(--ds-space-2)" : "var(--ds-space-4)",
            gap: "var(--ds-space-2)",
          }}>
            {compact ? (
              <b style={{ fontSize: "var(--ds-text-md)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</b>
            ) : (
              <h2 className="ds-heading">{title}</h2>
            )}
            {compact ? (
              <button className="ds-btn ds-btn--secondary ds-btn--sm" style={{ flexShrink: 0 }} onClick={onClose}>Done</button>
            ) : (
              <button className="ds-iconbtn ds-iconbtn--plain" onClick={onClose} aria-label="Close">
                <svg viewBox="0 0 24 24"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            )}
          </div>
        </div>

        <div style={{
          flex: 1, minHeight: 0, overflowY: "auto",
          padding: compact ? "0 var(--ds-space-4) var(--ds-space-3)" : "0 var(--ds-space-5) var(--ds-space-5)",
        }}>
          {children}
        </div>
      </div>
    </div>
  );
}

export const Chevron = (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="var(--ds-text-muted)"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="m10 6 6 6-6 6" />
  </svg>
);
